import React from 'react';

// FDI Pediatric (Primary/Deciduous) Numbering System
// Quadrant 5 (upper right): 55-51, Quadrant 6 (upper left): 61-65
// Quadrant 8 (lower right): 85-81, Quadrant 7 (lower left): 71-75
const UPPER_TEETH = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65];
const LOWER_TEETH = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75];

const TOOTH_NAMES = {
  55: '2nd Molar', 54: '1st Molar', 53: 'Canine', 52: 'Lateral Incisor', 51: 'Central Incisor',
  61: 'Central Incisor', 62: 'Lateral Incisor', 63: 'Canine', 64: '1st Molar', 65: '2nd Molar',
  75: '2nd Molar', 74: '1st Molar', 73: 'Canine', 72: 'Lateral Incisor', 71: 'Central Incisor',
  81: 'Central Incisor', 82: 'Lateral Incisor', 83: 'Canine', 84: '1st Molar', 85: '2nd Molar',
};

const getToothType = (num) => {
  const unit = num % 10;
  if (unit >= 4) return 'molar';
  if (unit === 3) return 'canine';
  return 'incisor';
};

const ToothShape = ({ num, x, y, isUpper, isSelected, isTreated, isMultiTreated, onClick, interactive, treatmentInfo }) => {
  const type = getToothType(num);
  let w = 32, h = 36;
  if (type === 'molar') { w = 38; h = 40; }
  else if (type === 'canine') { w = 30; h = 38; }
  else { w = 28; h = 34; }

  let fill = '#fef9f0';
  let stroke = '#d4a574';
  if (isSelected) { fill = '#7c3aed'; stroke = '#7c3aed'; }
  else if (isMultiTreated) { fill = '#22C55E'; stroke = '#16A34A'; }
  else if (isTreated) { fill = '#E07A5F'; stroke = '#c0583f'; }

  const rootH = isUpper ? -10 : 10;

  return (
    <g onClick={interactive ? () => onClick(num) : undefined} style={{ cursor: interactive ? 'pointer' : 'default' }} data-testid={`pedo-tooth-${num}`}>
      <title>{`${num} - ${TOOTH_NAMES[num]}${treatmentInfo ? '\n' + treatmentInfo : ''}`}</title>
      <line x1={x} y1={y + (isUpper ? -h/2 : h/2)} x2={x} y2={y + (isUpper ? -h/2 : h/2) + rootH} stroke={stroke} strokeWidth="2" opacity="0.4" />
      <rect x={x - w/2} y={y - h/2} width={w} height={h} rx={8} ry={8} fill={fill} stroke={stroke} strokeWidth="1.5" className="transition-all duration-200" />
      <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="700" fill={isSelected || isTreated ? '#fff' : '#92400e'}>
        {num}
      </text>
    </g>
  );
};

const PedoDentalChart = ({ selectedTeeth = [], onToggleTooth, interactive = false, treatedTeethMap = {}, multiTreatTeeth = [] }) => {
  const spacing = 50;
  const startX = 40;
  const chartWidth = startX * 2 + spacing * 9;

  return (
    <div className="w-full overflow-x-auto" data-testid="pedo-dental-chart">
      <div className="text-center mb-2">
        <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">Pediatric Chart (Primary Teeth)</span>
      </div>
      <svg viewBox={`0 0 ${chartWidth} 210`} className="w-full max-w-[600px] mx-auto" style={{ minWidth: '400px' }}>
        <text x={chartWidth / 2} y={16} textAnchor="middle" fontSize="12" fill="#92400e" fontWeight="600">Upper</text>
        <text x={chartWidth / 2} y={200} textAnchor="middle" fontSize="12" fill="#92400e" fontWeight="600">Lower</text>
        <line x1={chartWidth / 2} y1={95} x2={chartWidth / 2} y2={115} stroke="#d4a574" strokeWidth="1" />
        <line x1={20} y1={105} x2={chartWidth - 20} y2={105} stroke="#d4a574" strokeWidth="1" strokeDasharray="4" />
        <text x={12} y={60} textAnchor="middle" fontSize="10" fill="#d4a574" fontWeight="600">R</text>
        <text x={chartWidth - 12} y={60} textAnchor="middle" fontSize="10" fill="#d4a574" fontWeight="600">L</text>
        <text x={12} y={155} textAnchor="middle" fontSize="10" fill="#d4a574" fontWeight="600">R</text>
        <text x={chartWidth - 12} y={155} textAnchor="middle" fontSize="10" fill="#d4a574" fontWeight="600">L</text>

        {UPPER_TEETH.map((num, i) => (
          <ToothShape key={num} num={num} x={startX + i * spacing} y={57} isUpper={true}
            isSelected={selectedTeeth.includes(num)} isTreated={!!treatedTeethMap[num] && !selectedTeeth.includes(num)}
            isMultiTreated={multiTreatTeeth.includes(num) && !selectedTeeth.includes(num)}
            onClick={onToggleTooth} interactive={interactive} treatmentInfo={treatedTeethMap[num]} />
        ))}
        {LOWER_TEETH.map((num, i) => (
          <ToothShape key={num} num={num} x={startX + i * spacing} y={152} isUpper={false}
            isSelected={selectedTeeth.includes(num)} isTreated={!!treatedTeethMap[num] && !selectedTeeth.includes(num)}
            isMultiTreated={multiTreatTeeth.includes(num) && !selectedTeeth.includes(num)}
            onClick={onToggleTooth} interactive={interactive} treatmentInfo={treatedTeethMap[num]} />
        ))}
      </svg>
      {interactive && selectedTeeth.length > 0 && (
        <div className="mt-2 text-sm text-purple-600 text-center font-medium">
          Selected: {selectedTeeth.sort((a,b) => a-b).join(', ')}
        </div>
      )}
    </div>
  );
};

export { TOOTH_NAMES as PEDO_TOOTH_NAMES };
export default PedoDentalChart;
